<template>
  <div class="w-100 permission-role-page" :class="{ 'is-scrolled': isScrolled }">
    <basic-subheader v-if="isAssignPermission">
      <template v-slot:filters>
        <transition name="fade">
          <div v-if="selectedDept && isScrolled" class="d-flex flex-column align-items-center" style="gap:6px">
            <div class="d-flex align-items-center">
              <span class="perm-sticky-dept-icon mr-2" :style="{ color: getDeptColor(selectedDepartmentId) }">
                <i :class="getDeptIcon(selectedDepartmentId)" />
              </span>
              <span class="perm-sticky-dept-name">{{ selectedDept.name }}</span>
            </div>
            <div class="d-flex flex-wrap" style="gap:6px">
              <span
                v-for="role in selectedDeptRoles"
                :key="role.id"
                class="dept-role-chip"
                :class="{ 'is-selected': selectedRoleId === role.roleId }"
                :style="{ '--role-color': getRoleColor(role.roleName) }"
                @click="toggleRoleFilter(role.roleId)"
              >{{ getDisplayRoleName(role.roleName) }}</span>
              <span v-if="!selectedDeptRoles.length" class="dept-roles-empty">Chưa có role nào</span>
            </div>
          </div>
        </transition>
      </template>
      <template v-slot:actions>
        <b-row>
          <b-col>
            <div class="d-flex justify-content-end">
              <b-button v-if="hasChanges" class="btn btn-success mr-4" type="button" @click="save" :disabled="saving">
                <b-spinner v-if="saving" small class="mr-1" />
                <span v-else class="svg-icon">
                  <inline-svg src="/media/svg/icons/Neolex/Basic/save.svg" />
                </span>
                Lưu lại
              </b-button>
              <b-button class="btn btn-light mr-2" type="button" @click="$bvModal.show('modal-guide')">
                <span class="svg-icon">
                  <inline-svg src="/media/svg/icons/Neolex/Basic/info.svg" />
                </span>
                Hướng dẫn
              </b-button>
              <b-button class="btn btn-success" type="button" @click="addRole">
                <span class="svg-icon">
                  <inline-svg src="/media/svg/icons/Neolex/Basic/plus.svg" />
                </span>
                Thêm mới
              </b-button>
              <b-button class="btn btn-light ml-2" type="button" @click="refresh" :disabled="loading">
                <b-spinner v-if="loading" small class="mr-1" />
                <span v-else class="svg-icon">
                  <inline-svg src="/media/svg/icons/Neolex/Basic/refresh-cw.svg" />
                </span>
                Làm mới
              </b-button>
            </div>
          </b-col>
        </b-row>
      </template>
    </basic-subheader>

    <Wrapper>
      <!-- Initial Loading State -->
      <div v-if="initialLoading" class="initial-loading-state">
        <div class="loading-content">
          <b-spinner variant="primary" style="width: 3rem; height: 3rem;" />
          <p class="loading-text mt-3">Đang tải dữ liệu phân quyền...</p>
          <div class="loading-progress mt-2">
            <div class="progress-item" :class="{ done: loadingSteps.headers }">
              <i :class="loadingSteps.headers ? 'fas fa-check-circle text-success' : 'fas fa-circle-notch fa-spin'" />
              <span>Tải danh sách vai trò</span>
            </div>
            <div class="progress-item" :class="{ done: loadingSteps.permissions }">
              <i :class="loadingSteps.permissions ? 'fas fa-check-circle text-success' : 'fas fa-circle-notch fa-spin'" />
              <span>Tải danh sách quyền</span>
            </div>
            <div class="progress-item" :class="{ done: loadingSteps.selectedRoles }">
              <i :class="loadingSteps.selectedRoles ? 'fas fa-check-circle text-success' : 'fas fa-circle-notch fa-spin'" />
              <span>Tải phân quyền hiện tại</span>
            </div>
            <div class="progress-item" :class="{ done: loadingSteps.departments }">
              <i :class="loadingSteps.departments ? 'fas fa-check-circle text-success' : 'fas fa-circle-notch fa-spin'" />
              <span>Tải danh sách phòng ban</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Main Content -->
      <div v-else>
        <div class="dept-role-layout">
          <!-- Tree panel -->
          <DeptTreePanel
            :departments="departments"
            :selected-department-id="selectedDepartmentId"
            @select="selectDepartment"
            @edit="editDept"
            @delete="confirmDeleteDept"
            @search="deptTreeSearch = $event"
          />

          <!-- Permission table panel -->
          <div class="dept-permission-panel">
            <div v-if="!selectedDepartmentId" class="dept-empty-state">
              <inline-svg src="/media/svg/Permission-role/User-circle-gear.svg" class="dept-empty-icon" />
              <p class="dept-empty-title">Chọn phòng ban</p>
              <p class="dept-empty-sub">Chọn một phòng ban bên trái để xem và chỉnh sửa phân quyền theo vai trò</p>
            </div>
            <template v-else>
              <div ref="deptInfoBar" class="perm-dept-info-bar">
                <div class="d-flex align-items-center mb-1">
                  <span class="perm-sticky-dept-icon mr-2" :style="{ color: getDeptColor(selectedDepartmentId) }">
                    <i :class="getDeptIcon(selectedDepartmentId)" />
                  </span>
                  <span class="perm-sticky-dept-name">{{ selectedDept && selectedDept.name }}</span>
                </div>
                <div class="d-flex flex-wrap" style="gap:6px">
                  <span
                    v-for="role in selectedDeptRoles"
                    :key="role.id"
                    class="dept-role-chip"
                    :class="{ 'is-selected': selectedRoleId === role.roleId }"
                    :style="{ '--role-color': getRoleColor(role.roleName) }"
                    @click="toggleRoleFilter(role.roleId)"
                  >{{ getDisplayRoleName(role.roleName) }}</span>
                  <span v-if="!selectedDeptRoles.length" class="dept-roles-empty">Chưa có role nào</span>
                </div>
              </div>
              <b-overlay :show="loading" spinner-variant="primary">
                <PermissionTable
                  :all-headers="allHeaders"
                  :grouped-permissions="groupedPermissions"
                  :closed-groups="closedGroups"
                  :visible-role-ids="visibleRoleIds"
                  :is-assign-permission="isAssignPermission"
                  :permissions="permissions"
                  @toggle-expand-all="toggleExpandAll"
                  @toggle-group="toggleGroupCollapse"
                  @toggle-select-all="toggleSelectAll"
                  @edit-role="openEditRole"
                  @role-change="handleRoleChange"
                />
              </b-overlay>
            </template>
          </div>
        </div>
      </div>
    </Wrapper>

    <ModalRole
      :departments="departments"
      @add-success="addSuccess"
      @department-created="onDepartmentCreated"
    />
    <DepartmentForm :department="editingDept" @save-success="onDeptSaved" @reset="editingDept = null" />

    <b-modal
      id="modal-edit-role"
      title="Sửa vai trò"
      size="sm"
      centered
      hide-footer
      @hide="editingRole = null"
    >
      <b-overlay :show="editRoleLoading">
        <form @submit.prevent="submitEditRole">
          <basic-input
            :required="true"
            label="Tên vai trò"
            name="editRoleName"
            placeholder="Nhập tên vai trò"
            :value.sync="editRoleName"
            maxlength="20"
          />
          <div class="d-flex justify-content-between mt-3">
            <b-button class="btn btn-danger" type="button" @click="deleteRoleFromEdit">
              <i class="fas fa-trash-alt mr-1" />Xóa
            </b-button>
            <div class="d-flex">
              <b-button class="btn btn-success" type="submit">
                <span class="svg-icon"><inline-svg src="/media/svg/icons/Neolex/Basic/save.svg" /></span>
                Lưu
              </b-button>
            </div>
          </div>
        </form>
      </b-overlay>
    </b-modal>

    <b-modal
      id="modal-confirm-delete-dept"
      title="Xóa phòng ban"
      size="sm"
      centered
      hide-footer
    >
      <div class="mb-4">
        Bạn có chắc chắn muốn xóa phòng ban <strong>{{ deletingDept && deletingDept.name }}</strong> không?
      </div>
      <div class="d-flex justify-content-end">
        <b-button class="btn mr-3" @click="$bvModal.hide('modal-confirm-delete-dept')">Hủy</b-button>
        <b-button class="btn btn-danger" :disabled="deleteLoading" @click="deleteDept">
          <b-spinner v-if="deleteLoading" small class="mr-1" />
          Xóa
        </b-button>
      </div>
    </b-modal>

    <b-modal id="modal-guide" title="Hướng dẫn thao tác" size="md" centered hide-footer>
      <div class="guide-section mb-4">
        <div class="d-flex align-items-center mb-3">
          <div class="role mr-3">
            <inline-svg class="svg-icon" src="/media/svg/Permission-role/User-circle-gear.svg" />
          </div>
          <h6 class="mb-0">Các quyền hiện có</h6>
        </div>
        <p><b>View: </b>Cho phép người dùng truy cập và xem các bản ghi</p>
        <p><b>Insert: </b>Cho phép người dùng tạo mới bản ghi</p>
        <p><b>Update: </b>Cho phép người dùng chỉnh sửa bản ghi</p>
        <p><b>Delete: </b>Cho phép người dùng xóa bản ghi</p>
        <p class="mb-0"><b>Assign: </b>Cho phép người dùng gán dữ liệu (chỉ dành cho Admin)</p>
      </div>
      <hr />
      <div class="guide-section mt-3">
        <div class="d-flex align-items-center mb-3">
          <div class="permission mr-3">
            <inline-svg class="svg-icon" src="/media/svg/icons/Neolex/Basic/info.svg" />
          </div>
          <h6 class="mb-0">Hướng dẫn sử dụng</h6>
        </div>
        <p><b>Chọn tất cả:</b> Cấp toàn bộ quyền cho vai trò</p>
        <p><b>Bỏ chọn tất cả:</b> Thu hồi toàn bộ quyền của vai trò</p>
        <p><b>Xóa vai trò:</b> Xóa vai trò khỏi hệ thống</p>
        <p><b>Thêm mới:</b> Tạo vai trò mới</p>
        <p class="mb-0"><b>Lưu lại:</b> Áp dụng thay đổi</p>
      </div>
    </b-modal>
  </div>
</template>

<script>
import groupBy from 'lodash/groupBy';
import { MODAL_STATUS, PAGE_CODE, PERMISSION_TYPE } from '@/core/plugins/constants';
import { ADD_BODY_CLASSNAME, REMOVE_BODY_CLASSNAME } from '@/core/services/store/htmlclass.module';
import DepartmentService from './services/department.service';

export default {
  name: 'permission_role',
  components: {
    ModalRole: () => import('./components/ModalRole.vue'),
    DepartmentForm: () => import('./components/DepartmentForm.vue'),
    PermissionTable: () => import('./components/PermissionTable.vue'),
    DeptTreePanel: () => import('./components/DeptTreePanel.vue'),
  },
  data() {
    return {
      allHeaders: [],
      permissions: [],
      selectedRoles: [],
      loading: false,
      saving: false,
      initialLoading: true,
      loadingSteps: {
        headers: false,
        permissions: false,
        selectedRoles: false,
        departments: false,
      },
      PAGE_CODE,
      PERMISSION_TYPE,
      departments: [],
      selectedDepartmentId: null,
      deptTreeSearch: '',
      selectedRoleId: null,
      cachedPermissions: [],
      closedGroups: [],
      deletingDept: null,
      deleteLoading: false,
      isScrolled: false,
      editingDept: null,
      editingRole: null,
      editRoleName: '',
      editRoleLoading: false,
      // Cache maps for performance
      permissionByIdMap: new Map(),
      deptColorCache: new Map(),
      deptIconCache: new Map(),
    };
  },
  computed: {
    allRoleHeaders() {
      return this.allHeaders.filter((h) => h.id);
    },
    groupedPermissions() {
      if (!this.permissions.length) return [];
      // Define menu order
      const menuOrder = {
        'Quản lý tài khoản': 100,
        'Quản lý khách hàng': 200,
        'Quản lý khóa học': 300,
        'Quản lý khảo sát': 400,
        'Quản lý đặt lịch': 500,
        'Cổng giao tiếp': 600,
        'Chương trình': 700,
        'Công cụ': 800,
        'Clinic Bundle': 900,
        'BCB Campaign': 1000,
        'Cài đặt': 1100,
        'His Doctor': 1200,
        'Thử nghiệm': 1300,
        'OTHER': 9900,
      };
      // Group by menuGroup first (level 1)
      const byMenuGroup = groupBy(this.permissions, 'menuGroup');
      return Object.keys(byMenuGroup)
        .sort((a, b) => (menuOrder[a] || 9999) - (menuOrder[b] || 9999))
        .map((menuGroup) => {
          // Group by groupName within menuGroup (level 2)
          const byGroupName = groupBy(byMenuGroup[menuGroup], 'groupName');
          const subGroups = Object.keys(byGroupName)
            .sort((a, b) => byGroupName[a][0].order - byGroupName[b][0].order)
            .map((groupName) => ({
              groupName,
              items: byGroupName[groupName].sort((a, b) => a.order - b.order),
            }));
          return {
            menuGroup,
            subGroups,
            featureCount: subGroups.length,
          };
        });
    },
    selectedDept() {
      if (!this.selectedDepartmentId) return null;
      return this.departments.find((d) => d.id === this.selectedDepartmentId) || null;
    },
    selectedDeptRoles() {
      if (!this.selectedDept || !this.selectedDept.roles) return [];
      // Sort by role order: Staff, Support, Leader, Manager
      const roleOrder = { Staff: 1, Support: 2, Leader: 3, Manager: 4, Admin: 5 };
      const getShortName = (name) => {
        if (!name) return name;
        const idx = name.indexOf('_');
        return idx > 0 ? name.substring(0, idx) : name;
      };
      return [...this.selectedDept.roles].sort((a, b) => {
        const orderA = roleOrder[getShortName(a.roleName)] || 99;
        const orderB = roleOrder[getShortName(b.roleName)] || 99;
        return orderA - orderB;
      });
    },
    visibleRoleIds() {
      if (!this.selectedDepartmentId) {
        return new Set(this.allHeaders.filter((h) => h.id).map((h) => h.id));
      }
      const dept = this.departments.find((d) => d.id === this.selectedDepartmentId);
      if (!dept || !dept.roles || !dept.roles.length) return new Set();
      let ids = dept.roles.map((r) => r.roleId);
      if (this.selectedRoleId) ids = ids.filter((id) => id === this.selectedRoleId);
      return new Set(ids);
    },
    hasChanges() {
      return this.permissions.some((p) =>
        p.roles.some((r) => r.modelStatus !== MODAL_STATUS.UNCHANGE),
      );
    },
  },
  created() {
    this.loadData();
    this.$store.dispatch(ADD_BODY_CLASSNAME, 'aside-minimize');
    this.$store.dispatch('layout/toggleSidebar', false);
  },
  mounted() {
    this._onScroll = () => { this.isScrolled = window.scrollY > 10; };
    window.addEventListener('scroll', this._onScroll);
    this.$nextTick(() => {
      if (this.$refs.deptInfoBar) {
        this._observer = new IntersectionObserver(
          ([entry]) => { this.isScrolled = !entry.isIntersecting; },
          { threshold: 0, rootMargin: '-80px 0px 0px 0px' }
        );
        this._observer.observe(this.$refs.deptInfoBar);
      }
    });
  },
  beforeDestroy() {
    this.$store.dispatch(REMOVE_BODY_CLASSNAME, 'aside-minimize');
    this.$store.dispatch('layout/toggleSidebar', true);
    window.removeEventListener('scroll', this._onScroll);
    if (this._observer) this._observer.disconnect();
  },
  methods: {
    selectDepartment(id) {
      this.selectedDepartmentId = id;
      this.selectedRoleId = null;
    },
    editDept(dept) {
      this.editingDept = dept;
      this.$bvModal.show('modal-department-form');
    },
    confirmDeleteDept(dept) {
      this.deletingDept = dept;
      this.$bvModal.show('modal-confirm-delete-dept');
    },
    async deleteDept() {
      try {
        this.deleteLoading = true;
        const { meta, error } = await DepartmentService.remove(this.$api, this.deletingDept.id);
        if (!meta.success) {
          return this.$toastr.e({ title: 'Lỗi !', msg: error.message, preventDuplicates: true });
        }
        this.$toastr.s({ title: 'Thành công !', msg: 'Xóa phòng ban thành công' });
        if (this.selectedDepartmentId === this.deletingDept.id) {
          this.selectedDepartmentId = null;
          this.selectedRoleId = null;
        }
        this.$bvModal.hide('modal-confirm-delete-dept');
        await this.loadDepartments();
      } catch (error) {
        this.$toastr.e({ title: 'Lỗi !', msg: error.message, preventDuplicates: true });
      } finally {
        this.deleteLoading = false;
      }
    },
    toggleGroupCollapse(key) {
      const idx = this.closedGroups.indexOf(key);
      if (idx >= 0) this.closedGroups.splice(idx, 1);
      else this.closedGroups.push(key);
    },
    toggleExpandAll() {
      if (this.closedGroups.length === 0) {
        // Collapse all: menuGroups + all subGroups
        const allKeys = [];
        this.groupedPermissions.forEach((mg) => {
          allKeys.push(mg.menuGroup);
          mg.subGroups.forEach((sg) => {
            allKeys.push(mg.menuGroup + '|' + sg.groupName);
          });
        });
        this.closedGroups = allKeys;
      } else {
        this.closedGroups = [];
      }
    },
    async loadData(showToast = false) {
      const isInitial = this.initialLoading;
      if (!isInitial) this.loading = true;

      try {
        // Load in parallel with progress tracking
        await Promise.all([
          this.getHeaders().then(() => { this.loadingSteps.headers = true; }),
          this.getPermissionsRaw().then(() => { this.loadingSteps.permissions = true; }),
          this.getSelectedRoles().then(() => { this.loadingSteps.selectedRoles = true; }),
          this.loadDepartments().then(() => { this.loadingSteps.departments = true; }),
        ]);

        // Small delay to show completion state
        if (isInitial) {
          await new Promise(resolve => setTimeout(resolve, 300));
        }

        this.rebuildPermissions();

        if (showToast) {
          this.$toastr.s({ title: 'Thành công !', msg: 'Dữ liệu đã được cập nhật' });
        }
      } finally {
        this.loading = false;
        this.initialLoading = false;
      }
    },
    async getHeaders() {
      try {
        const { meta, error, data } = await this.$api.get('/Role');
        if (!meta.success) {
          return this.$toastr.e({
            title: 'Lỗi !',
            msg: error.message,
            preventDuplicates: true,
          });
        }

        // Sort by role order: Staff, Support, Leader, Manager, Admin
        const roleOrder = { Staff: 1, Support: 2, Leader: 3, Manager: 4, Admin: 5 };
        const getShortName = (name) => {
          if (!name) return name;
          const idx = name.indexOf('_');
          return idx > 0 ? name.substring(0, idx) : name;
        };

        const roleHeaders = data
          .map((el) => ({
            text: el.name,
            align: 'center',
            sortable: false,
            value: 'role',
            id: el.id,
            code: el.code,
          }))
          .sort((a, b) => {
            const orderA = roleOrder[getShortName(a.text)] || 99;
            const orderB = roleOrder[getShortName(b.text)] || 99;
            return orderA - orderB;
          });

        const firstCol = {
          text: 'Tính năng',
          align: 'start',
          sortable: false,
          value: 'permission',
          width: '10%',
        };
        roleHeaders.unshift(firstCol);
        this.allHeaders = roleHeaders;
      } catch (error) {
        this.$toastr.e({
          title: 'Lỗi !',
          msg: error.message,
          preventDuplicates: true,
        });
      }
    },
    async getPermissionsRaw() {
      try {
        const { meta, error, data } = await this.$api.get('/PermissionRoleNew/Permissions');
        if (!meta.success) {
          return this.$toastr.e({ title: 'Lỗi !', msg: error.message, preventDuplicates: true });
        }
        this.cachedPermissions = data;

        // Build lookup map
        this.permissionByIdMap.clear();
        data.forEach(p => this.permissionByIdMap.set(p.id, p));
      } catch (error) {
        this.$toastr.e({ title: 'Lỗi !', msg: error.message, preventDuplicates: true });
      }
    },
    rebuildPermissions() {
      const allRoleHeaders = this.allHeaders.filter((h) => h.id);

      // Build selectedRoles lookup map for O(1) access
      const selectedRolesMap = new Map();
      this.selectedRoles.forEach(s => {
        const key = `${s.permissionId}-${s.roleId}`;
        selectedRolesMap.set(key, s);
      });

      this.permissions = this.cachedPermissions.map((el) => ({
        ...el,
        roles: allRoleHeaders.map((role) => {
          const key = `${el.id}-${role.id}`;
          const selected = selectedRolesMap.has(key);
          return {
            roleId: role.id,
            checked: selected,
            modelStatus: MODAL_STATUS.UNCHANGE,
            oldChecked: selected,
            permissionId: el.id,
            nameType: el.nameType,
            roleCode: role.code,
            roleName: role.text,
          };
        }),
      }));

      const groupRole = groupBy(this.selectedRoles, 'roleId');
      this.allHeaders.forEach((role) => {
        if (!role.id) return;
        const permission = groupRole[role.id] || [];
        role.checked = permission.length === this.cachedPermissions.length;
      });

      if (!this._initialCollapseSet) {
        // Collapse all menuGroups initially
        this.closedGroups = this.groupedPermissions.map((g) => g.menuGroup);
        this._initialCollapseSet = true;
      }
    },
    async getSelectedRoles() {
      try {
        const { meta, error, data } = await this.$api.get('/PermissionRoleNew');
        if (!meta.success) {
          return this.$toastr.e({
            title: 'Lỗi !',
            msg: error.message,
            preventDuplicates: true,
          });
        }
        this.selectedRoles = data;
      } catch (error) {
        this.$toastr.e({
          title: 'Lỗi !',
          msg: error.message,
          preventDuplicates: true,
        });
      }
    },
    async loadDepartments() {
      const { meta, data } = await DepartmentService.getAll(this.$api);
      if (meta.success) {
        this.departments = data || [];
        this.buildDeptCaches();
      }
    },
    buildDeptCaches() {
      const icons = [
        'fas fa-laptop-code', 'fas fa-heartbeat', 'fas fa-chart-bar', 'fas fa-users',
        'fas fa-shield-alt', 'fas fa-cogs', 'fas fa-microscope', 'fas fa-graduation-cap',
        'fas fa-briefcase', 'fas fa-stethoscope', 'fas fa-database', 'fas fa-paint-brush',
      ];
      const colors = ['#3699ff', '#0bb783', '#f64e60', '#ffa800', '#8950fc', '#1bc5bd', '#e4b40c', '#ee6c4d'];

      this.deptColorCache.clear();
      this.deptIconCache.clear();

      this.departments.forEach(d => {
        const hash = d.id.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
        this.deptColorCache.set(d.id, colors[hash % colors.length]);
        this.deptIconCache.set(d.id, icons[hash % icons.length]);
      });
    },
    toggleRoleFilter(roleId) {
      this.selectedRoleId = this.selectedRoleId === roleId ? null : roleId;
    },
    addRole() {
      this.$bvModal.show('modal-role');
    },
    async refresh() {
      this.loadingSteps = { headers: false, permissions: false, selectedRoles: false, departments: false };
      await this.loadData(true);
    },
    addSuccess() {
      this.loadData();
    },
    onDepartmentCreated(dept) {
      this.departments.push(dept);
      this.buildDeptCaches();
    },
    onDeptSaved() {
      this.loadDepartments();
    },
    getDisplayRoleName(fullName) {
      // Leader_Technology -> Leader
      if (!fullName) return fullName;
      const idx = fullName.indexOf('_');
      return idx > 0 ? fullName.substring(0, idx) : fullName;
    },
    getRoleOrder(roleName) {
      // Thứ tự hiển thị: Staff, Support, Leader, Manager
      const order = { Staff: 1, Support: 2, Leader: 3, Manager: 4, Admin: 5 };
      const shortName = this.getDisplayRoleName(roleName);
      return order[shortName] || 99;
    },
    getDeptIcon(id) {
      if (this.deptIconCache.has(id)) return this.deptIconCache.get(id);
      const icons = [
        'fas fa-laptop-code', 'fas fa-heartbeat', 'fas fa-chart-bar', 'fas fa-users',
        'fas fa-shield-alt', 'fas fa-cogs', 'fas fa-microscope', 'fas fa-graduation-cap',
        'fas fa-briefcase', 'fas fa-stethoscope', 'fas fa-database', 'fas fa-paint-brush',
      ];
      const hash = id.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
      const icon = icons[hash % icons.length];
      this.deptIconCache.set(id, icon);
      return icon;
    },
    getDeptColor(id) {
      if (this.deptColorCache.has(id)) return this.deptColorCache.get(id);
      const colors = ['#3699ff', '#0bb783', '#f64e60', '#ffa800', '#8950fc', '#1bc5bd', '#e4b40c', '#ee6c4d'];
      const hash = id.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
      const color = colors[hash % colors.length];
      this.deptColorCache.set(id, color);
      return color;
    },
    getRoleColor(roleName) {
      // Màu cố định cho từng role type
      const colors = {
        Staff: '#3699ff',    // Xanh dương
        Support: '#0bb783',  // Xanh lá
        Leader: '#8950fc',   // Tím
        Manager: '#f64e60',  // Đỏ
        Admin: '#ffa800',    // Cam
      };
      const shortName = this.getDisplayRoleName(roleName);
      return colors[shortName] || '#7e8299'; // Màu mặc định xám cho role mới
    },
    openEditRole(role) {
      this.editingRole = role;
      this.editRoleName = role.text;
      this.$nextTick(() => this.$bvModal.show('modal-edit-role'));
    },
    async deleteRoleFromEdit() {
      const role = this.editingRole;
      this.$bvModal.hide('modal-edit-role');
      await this.deleteRole(role);
    },
    async submitEditRole() {
      if (!this.editRoleName || !this.editRoleName.trim()) return;
      try {
        this.editRoleLoading = true;
        const { meta, error } = await this.$api.put(`/Role/${this.editingRole.id}`, {
          name: this.editRoleName,
          code: this.editRoleName,
        });
        if (!meta.success) {
          return this.$toastr.e({ title: 'Lỗi !', msg: error.message, preventDuplicates: true });
        }
        this.$toastr.s({ title: 'Thành công !', msg: 'Cập nhật vai trò thành công' });
        this.$bvModal.hide('modal-edit-role');
        await this.loadData();
      } catch (error) {
        this.$toastr.e({ title: 'Lỗi !', msg: error.message, preventDuplicates: true });
      } finally {
        this.editRoleLoading = false;
      }
    },
    handleRoleChange({ checked, role, item }) {
      this.editRoleCheckbox(checked, role, item);
    },
    editRoleCheckbox(value, role, item) {
      role.checked = value;
      if (role.oldChecked === value) {
        role.modelStatus = MODAL_STATUS.UNCHANGE;
      } else {
        role.modelStatus = value ? MODAL_STATUS.NEW : MODAL_STATUS.DELETE;
      }

      // Auto-tick related permissions when Admin/Manager ticks Assign
      const shortRoleName = this.getDisplayRoleName(role.roleName);
      if (value && role.nameType === PERMISSION_TYPE.ASSIGN && (shortRoleName === 'Admin' || shortRoleName === 'Manager')) {
        const currentPermission = item || this.permissions.find((p) => p.id === role.permissionId);
        if (currentPermission) {
          this.permissions
            .filter((p) => p.order === currentPermission.order && p.nameType !== PERMISSION_TYPE.ASSIGN)
            .forEach((p) => {
              const siblingRole = p.roles.find((r) => r.roleId === role.roleId);
              if (siblingRole && !siblingRole.checked) {
                siblingRole.checked = true;
                siblingRole.modelStatus = siblingRole.oldChecked ? MODAL_STATUS.UNCHANGE : MODAL_STATUS.NEW;
              }
            });
        }
      }

      // Update header checked state
      const header = this.allHeaders.find((el) => el.id === role.roleId);
      if (header) {
        const allChecked = this.permissions.every(p =>
          p.roles.find(r => r.roleId === role.roleId)?.checked
        );
        header.checked = allChecked;
      }
    },
    isAllSelected(header) {
      return this.permissions.every((permission) =>
        permission.roles.every((role) => {
          if (role.roleId !== header.id) return true;
          const shortName = this.getDisplayRoleName(role.roleName);
          const isDisabled = shortName === 'Admin'
            ? permission.nameType !== PERMISSION_TYPE.ASSIGN
            : (shortName !== 'Manager' && permission.nameType === PERMISSION_TYPE.ASSIGN);
          if (isDisabled) return true;
          return role.checked;
        })
      );
    },
    toggleSelectAll(header) {
      this.selectedAll(!this.isAllSelected(header), header);
    },
    selectedAll(value, header) {
      this.permissions.forEach((permission) => {
        permission.roles.forEach((role) => {
          if (role.roleId !== header.id) return;
          const shortName = this.getDisplayRoleName(role.roleName);
          const isDisabled = shortName === 'Admin'
            ? permission.nameType !== PERMISSION_TYPE.ASSIGN
            : (shortName !== 'Manager' && permission.nameType === PERMISSION_TYPE.ASSIGN);
          if (isDisabled) return;
          role.checked = value;
          if (role.oldChecked === value) {
            role.modelStatus = MODAL_STATUS.UNCHANGE;
          } else {
            role.modelStatus = value ? MODAL_STATUS.NEW : MODAL_STATUS.DELETE;
          }
        });
      });
      header.checked = value;
    },
    async save() {
      let params = [];
      this.permissions.forEach((el) => {
        const changedRoles = el.roles.filter(
          (role) => role.modelStatus !== MODAL_STATUS.UNCHANGE,
        );
        params = [...params, ...changedRoles];
      });

      if (!params.length) return;

      params = params.map((el) => ({
        permissionId: el.permissionId,
        roleId: el.roleId,
        modelStatus: el.modelStatus,
      }));

      this.saving = true;
      try {
        const { meta, error } = await this.$api.put('/PermissionRoleNew', params);
        if (!meta.success) {
          this.$toastr.e({
            title: 'Lỗi !',
            msg: error.message,
            preventDuplicates: true,
          });
          return;
        }
        await this.loadData();
        this.$store.dispatch('GET_PERMISSIONS');
        this.$toastr.s({ title: 'Thành công !', msg: 'Lưu phân quyền thành công' });
      } catch (error) {
        this.$toastr.e({
          title: 'Lỗi !',
          msg: error.message,
          preventDuplicates: true,
        });
      } finally {
        this.saving = false;
      }
    },
    async deleteRole(role) {
      try {
        this.loading = true;
        const { meta, error } = await this.$api.delete(`/Role/${role.id}`);
        if (!meta.success) {
          return this.$toastr.e({
            title: 'Lỗi !',
            msg: error.message,
            preventDuplicates: true,
          });
        }
        await this.loadData();
        this.$toastr.s({ title: 'Thành công !', msg: 'Xóa vai trò thành công' });
      } catch (error) {
        this.$toastr.e({
          title: 'Lỗi !',
          msg: error.message,
          preventDuplicates: true,
        });
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss">
.permission-role-page .basic-subheader {
  position: sticky;
  top: 0;
  z-index: 100;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: padding 0.2s ease;
  justify-content: flex-end;
  min-height: 80px;
  align-items: center;

  .subheader-filters {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    pointer-events: auto;
    text-align: center;
  }
}

.permission-role-page.is-scrolled .basic-subheader {
  padding-left: 12px;
  padding-right: 12px;
  gap: 12px;
}
</style>

<style lang="scss" scoped>
.btn-danger::v-deep {
  background-color: #f64e60 !important;
  border-color: #f64e60 !important;
  color: #fff !important;
}

// Initial Loading State
.initial-loading-state {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 500px;
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e4e6ef;
}

.loading-content {
  text-align: center;
  padding: 40px;
}

.loading-text {
  font-size: 16px;
  font-weight: 500;
  color: #3f4254;
}

.loading-progress {
  display: flex;
  flex-direction: column;
  gap: 8px;
  text-align: left;
  max-width: 250px;
  margin: 0 auto;
}

.progress-item {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  color: #7e8299;
  padding: 6px 12px;
  border-radius: 6px;
  background: #f8f9fc;
  transition: all 0.3s ease;

  &.done {
    background: #e8fff3;
    color: #0bb783;
  }

  i {
    width: 16px;
    text-align: center;
  }
}

.role {
  width: 60px;
  height: 60px;
  min-width: 60px;
  background: #def2f1;
  border-radius: 8px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.permission {
  width: 60px;
  height: 60px;
  min-width: 60px;
  background: #fff7c0;
  border-radius: 8px;
  display: flex;
  justify-content: center;
  align-items: center;
}

// Department Tree Layout
.dept-role-layout {
  display: grid;
  grid-template-columns: 260px 1fr;
  gap: 0;
  min-height: 500px;
  border: 1px solid #e4e6ef;
  border-radius: 12px;
  overflow: hidden;
}

// Permission Panel
.dept-permission-panel {
  background: #fff;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  position: relative;
}

.dept-empty-state {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 40px;
  text-align: center;
}

.dept-empty-icon {
  width: 64px;
  height: 64px;
  opacity: 0.2;
  margin-bottom: 20px;
}

.dept-empty-title {
  font-size: 16px;
  font-weight: 600;
  color: #3f4254;
  margin-bottom: 8px;
}

.dept-empty-sub {
  font-size: 13px;
  color: #b5b5c3;
  max-width: 280px;
  line-height: 1.6;
}

// Sticky info bar
.perm-dept-info-bar {
  padding: 14px 20px;
  border-bottom: 1px solid #e4e6ef;
  background: #f8f9fc;
}

.fade-enter-active, .fade-leave-active { transition: opacity 0.2s; }
.fade-enter, .fade-leave-to { opacity: 0; }

.perm-sticky-dept-icon {
  font-size: 15px;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: #f3f6f9;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.perm-sticky-dept-name {
  font-size: 13px;
  font-weight: 700;
  color: #3f4254;
  white-space: nowrap;
}

.dept-role-chip {
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  background: color-mix(in srgb, var(--role-color, #008479) 12%, #fff);
  color: var(--role-color, #008479);
  border: 1px solid color-mix(in srgb, var(--role-color, #008479) 30%, #fff);
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: color-mix(in srgb, var(--role-color, #008479) 20%, #fff);
  }

  &.is-selected {
    background: var(--role-color, #008479);
    color: #fff;
    border-color: var(--role-color, #008479);
  }
}

.dept-roles-empty {
  font-size: 12px;
  color: #b5b5c3;
  font-style: italic;
}
</style>
